import "./dashboard.page.style.scss";
import { FC, useContext, useEffect, useState } from "react";
import di from "../../../dependency_injection";
import GetOperationReportUseCase, { GetOperationReportUseCaseName } from "../../../domain/use_cases/dashboard/get_operation_report.use_case";
import { isLeft } from "fp-ts/lib/Either";
import ModalsContext from "../../providers/modal/ModalsContext";
import ModalsContextType from "../../../domain/providers/modal/ModalsContextType";
import KeyWordLocalization from "../../utils/KeyWordLocalization";
import { GetOperationReportResponse } from "../../../domain/repositories/dashboard.repository";
import DatesUtils from "../../utils/dates_utils";
import LocalizationContext from "../../providers/localization/LocalizationContext";
import LocalizationContextType from "../../../domain/providers/localization/LocalizationContextType";
import MasterItemEntity from "../../../domain/entities/master_item.entity";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import routes from "../../routes/Routes";
import AgentAnalysisComponent from "./components/agent_analysis/agent_analysis.component";
import { AutoCompleteItem } from "../../components/autocompleteWithCheck/AutoCompleteWithCheckProps";
import HumanAgentEntity from "../../../domain/entities/human_agent.entity";
import SearchHumanAgentsUseCase, { SearchHumanAgentsUseCaseName } from "../../../domain/use_cases/human_agent/search_human_agents.use_case";
import QAAgentEntity from "../../../domain/entities/qa_agent.entity";
import SearchAgentsUseCase, { SearchAgentsUseCaseName } from "../../../domain/use_cases/qa/search_agents.use_case";
import GetHumanAgentByIdUseCase, { GetHumanAgentByIdUseCaseName } from "../../../domain/use_cases/human_agent/get_human_agent_by_id.use_case";
import GetAgentByIdUseCase, { GetAgentByIdUseCaseName } from "../../../domain/use_cases/qa/get_agent_by_id.use_case";
import SearchOperationTypesUseCase, { SearchOperationTypesUseCaseName } from "../../../domain/use_cases/qa/search_operation_types.use_case";
import MasterDataContext from "../../providers/master_data/master_data_context";
import MasterDataContextType from "../../../domain/providers/master_data/master_data_context_type";
import GetCallTypesUseCase, { GetCallTypesUseCaseName } from "../../../domain/use_cases/master_data/get_call_types_use_case";

const useDashboardPageViewModel = () => {
    const { openModalCustom, addToast } = useContext(ModalsContext) as ModalsContextType;
    const {typesCall} = useContext(MasterDataContext) as MasterDataContextType;

    const formInfo = useForm();
    const { register, handleSubmit, setValue, watch, getValues, formState: { errors } } = formInfo;
    const { i18n } = useContext(LocalizationContext) as LocalizationContextType;
    const [data, setData] = useState<GetOperationReportResponse | null | undefined>(null);
    const [loaded, setLoaded] = useState<boolean>(false);
    const navigate = useNavigate();
    const colors = ['#ff645e', '#8c8484', '#73bf94'];

    const queryParameters = new URLSearchParams(window.location.search);
    const opType = queryParameters.get(i18n(KeyWordLocalization.DashboardPageUrlParamOptype));
    const dateFrom = queryParameters.get(i18n(KeyWordLocalization.DashboardPageUrlParamDateFrom));
    const dateTo = queryParameters.get(i18n(KeyWordLocalization.DashboardPageUrlParamDateTo));
    const humanAgentQuery = queryParameters.get(i18n(KeyWordLocalization.DashboardPageUrilParamHumanAgent));
    const aiAgentQuery = queryParameters.get(i18n(KeyWordLocalization.DashboardPageUrilParamAiAgent));
    const callTypeQuery = queryParameters.get(i18n(KeyWordLocalization.DashboardPageUrilParamCallType));

    const _handleSeeAgentDetails = (agentId: string, agentName: string) => {
        const initDate = dateFrom ? new Date(dateFrom) : new Date();
        const endDate = dateTo ? new Date(dateTo) : new Date();
        openModalCustom("lg", i18n(KeyWordLocalization.TableSatisfactionDetailClient, { name: agentName }), <AgentAnalysisComponent agentId={agentId} endDate={endDate} startDate={initDate} />, {
            hideCardBody: true,
        });
    }

    const _getDashboardData = async () => {
        setData(undefined);
        setLoaded(false);
        const initDate = dateFrom ? new Date(dateFrom) : new Date();
        const endDate = dateTo ? new Date(dateTo) : new Date();
        if (dateFrom == null) initDate.setDate(initDate.getDate() - 30);
        const _callBack = (_da: GetOperationReportResponse) => {
            setData(_da);
        }
        const response = await di.get<GetOperationReportUseCase>(GetOperationReportUseCaseName).call({ startDate: initDate, endDate, aiAgentId: aiAgentQuery ?? undefined, callTypeId: callTypeQuery ?? undefined, humanAgentId: humanAgentQuery ?? undefined, operationId: opType ?? undefined }, _callBack);
        if (isLeft(response)) {
            setData(null);
            setLoaded(true);
            return addToast(response.left.code ?? KeyWordLocalization.UnknownError, "error", undefined);
        } else {
            setLoaded(true);
            setTimeout(() => {
                setValue(KeyWordLocalization.QAResumeEntityCallType, callTypeQuery);
            }, 100);
            setData(response.right);
        }
    }

    const parsedChartData = (): {
        labels: string[],
        datasets: {
            label: string,
            data: number[],
            backgroundColor: string
        }[]
    } => {
        const labels = data!.dayToDay!.map(day => DatesUtils.dayForGraphs(day.date));
        const datasets = [{
            label: i18n(KeyWordLocalization.DashboardPageSentimentNegative),
            data: data!.dayToDay!.map(day => day.sentiments.negative),
            backgroundColor: colors[0] + "33",
            borderColor: colors[0],
            fill: true,
        },
        {
            label: i18n(KeyWordLocalization.DashboardPageSentimentNeutral),
            data: data!.dayToDay!.map(day => day.sentiments.neutral),
            backgroundColor: colors[1] + "33",
            borderColor: colors[1],
            fill: true,
        },
        {
            label: i18n(KeyWordLocalization.DashboardPageSentimentPositive),
            data: data!.dayToDay!.map(day => day.sentiments.positive),
            backgroundColor: colors[2] + "33",
            borderColor: colors[2],
            fill: true,
        }];
        return {
            labels,
            datasets
        }
    }

    const _reduceFeelings = (feelingsList: number[]): number => {
        if (feelingsList.length === 0) return 0;
        const reduced = feelingsList.reduce((a, b) => a + b, 0);
        return Math.round(reduced);
    }

    const _getPercentFeelings = (feelingsList: number[]): number => {
        if ((data!.averages?.completed?.total == undefined || data?.averages?.completed?.total == 0) && !data?.totalSentiments?.negative && !data?.totalSentiments?.neutral && !data?.totalSentiments?.positive) return 0;
        const totalCalls = data?.averages?.completed?.total ?? ((data?.totalSentiments?.negative ?? 0) + (data?.totalSentiments?.neutral ?? 0) + (data?.totalSentiments?.positive ?? 0));
        const reduced = _reduceFeelings(feelingsList);
        if (reduced === 0) return 0;
        const percentage = (reduced / totalCalls) * 100;
        return Math.round(percentage);
    }


    const _handleLoadDefaults = () => {
        if (dateFrom) setValue(KeyWordLocalization.DashboardPageDateFromForm, dateFrom);
        if (dateTo) setValue(KeyWordLocalization.DashboardPageDateToForm, dateTo);
        if (opType) setValue(KeyWordLocalization.DashboardPageOpTypeFormId, opType);
        if (callTypeQuery) setValue(KeyWordLocalization.QAResumeEntityCallType, callTypeQuery);
        if (humanAgentQuery) setValue(KeyWordLocalization.DashboardPageHumanAgentId, humanAgentQuery);
        if (aiAgentQuery) setValue(KeyWordLocalization.DashboardPageAiAgentId, aiAgentQuery);
        _loadOpTypes();
        _loadHumanAgent();
        _loadAiAgent();
        _getDashboardData();
        _handleLoadCallTypes();

    }

    // const _onSubmit = (data: any) => {
    //     console.log("call submit", data);
    //     //add url params ?opType=1&dateFrom=2021-09-01&dateTo=2021-09-30
    //     const params = new URLSearchParams();
    //     if (data[KeyWordLocalization.DashboardPageOpTypeForm]) params.append(i18n(KeyWordLocalization.DashboardPageUrlParamOptype), data[KeyWordLocalization.DashboardPageOpTypeFormId]);
    //     if (data[KeyWordLocalization.DashboardPageDateFromForm]) params.append(i18n(KeyWordLocalization.DashboardPageUrlParamDateFrom), data[KeyWordLocalization.DashboardPageDateFromForm]);
    //     if (data[KeyWordLocalization.DashboardPageDateToForm]) params.append(i18n(KeyWordLocalization.DashboardPageUrlParamDateTo), data[KeyWordLocalization.DashboardPageDateToForm]);
    //     if (data[KeyWordLocalization.DashboardPageFormForm] && data[KeyWordLocalization.DashboardPageAiAgentId]) params.append(i18n(KeyWordLocalization.DashboardPageUrilParamAiAgent), data[KeyWordLocalization.DashboardPageAiAgentId]);
    //     if (data[KeyWordLocalization.QAResumeEntityHumanAgentName] && data[KeyWordLocalization.DashboardPageHumanAgentId]) params.append(i18n(KeyWordLocalization.DashboardPageUrilParamHumanAgent), data[KeyWordLocalization.DashboardPageHumanAgentId]);
    //     if (data[KeyWordLocalization.QAResumeEntityCallType]) params.append(i18n(KeyWordLocalization.DashboardPageUrilParamCallType), data[KeyWordLocalization.QAResumeEntityCallType]);
    //     const newUrl = routes.home.relativePath + "?" + params.toString();
    //     navigate(newUrl);
    // }

    const _onSubmit = (data: any) => {
        console.log("call submit", data);
        const params = new URLSearchParams();
    
        const opType = data[KeyWordLocalization.DashboardPageOpTypeFormId];
        if (opType != null && opType !== '') {
            params.append(
                i18n(KeyWordLocalization.DashboardPageUrlParamOptype),
                opType
            );
        }
    
        const dateFrom = data[KeyWordLocalization.DashboardPageDateFromForm];
        if (dateFrom) {
            params.append(
                i18n(KeyWordLocalization.DashboardPageUrlParamDateFrom),
                dateFrom
            );
        }
    
        const dateTo = data[KeyWordLocalization.DashboardPageDateToForm];
        if (dateTo) {
            params.append(
                i18n(KeyWordLocalization.DashboardPageUrlParamDateTo),
                dateTo
            );
        }
    
        const aiAgentId = data[KeyWordLocalization.DashboardPageAiAgentId];
        if (
            data[KeyWordLocalization.DashboardPageFormForm] &&
            aiAgentId != null && aiAgentId !== ''
        ) {
            params.append(
                i18n(KeyWordLocalization.DashboardPageUrilParamAiAgent),
                aiAgentId
            );
        }
    
        const humanAgentId = data[KeyWordLocalization.DashboardPageHumanAgentId];
        if (
            data[KeyWordLocalization.QAResumeEntityHumanAgentName] &&
            humanAgentId != null && humanAgentId !== ''
        ) {
            params.append(
                i18n(KeyWordLocalization.DashboardPageUrilParamHumanAgent),
                humanAgentId
            );
        }
    
        const callType = data[KeyWordLocalization.QAResumeEntityCallType];
        if (callType) {
            params.append(
                i18n(KeyWordLocalization.DashboardPageUrilParamCallType),
                callType
            );
        }
    
        const newUrl = routes.home.relativePath + '?' + params.toString();
        navigate(newUrl);
    };
    

    const _onSearchHuman = async (query: string): Promise<AutoCompleteItem<HumanAgentEntity>[]> => {
        const response = await di.get<SearchHumanAgentsUseCase>(SearchHumanAgentsUseCaseName).call({
            itemsPerPage: 20,
            page: 1,
            query,
            operationId: getValues()[KeyWordLocalization.DashboardPageOpTypeFormId],
        });
        if (isLeft(response)) return [];
        const parsed = response.right.data.map((item) => {
            return {
                label: item.name,
                id: item.id,
                aditionalValue: item,
            }
        });
        return parsed;
    };

    const onSearchAiAgent = async (query: string): Promise<AutoCompleteItem<QAAgentEntity>[]> => {
        console.log("query", getValues());
        const response = await di.get<SearchAgentsUseCase>(SearchAgentsUseCaseName).call({
            itemsPerPage: 20,
            page: 1,
            query,
            operationId: getValues()[KeyWordLocalization.DashboardPageOpTypeFormId],
        });
        if (isLeft(response)) return [];
        const parsed = response.right.data.map((item) => {
            return {
                label: item.name,
                id: item.id,
                aditionalValue: item,
            }
        });
        return parsed;
    };

    const handleChangeHumanAgent = (item: AutoCompleteItem<HumanAgentEntity>) => {
        setValue(KeyWordLocalization.DashboardPageHumanAgentId, item?.id);
        _onSubmit(getValues());
    };

    const handleChangeAiAgent = (item: AutoCompleteItem<QAAgentEntity>) => {
        setValue(KeyWordLocalization.DashboardPageAiAgentId, item?.id);
        _onSubmit(getValues());
    };

    const handleChangeForm = () => {
        _onSubmit(getValues());
    }


    const _loadHumanAgent = async () => {
        if (humanAgentQuery && getValues()[KeyWordLocalization.QAResumeEntityHumanAgentName] == "") {
            const response = await di.get<GetHumanAgentByIdUseCase>(GetHumanAgentByIdUseCaseName).call(humanAgentQuery);
            if (isLeft(response)) return;
            const parsed: AutoCompleteItem<HumanAgentEntity> = {
                label: response.right.name,
                id: response.right.id,
                aditionalValue: response.right,
            };
            setValue(KeyWordLocalization.QAResumeEntityHumanAgentName, [parsed]);
        }
    }

    const _loadOpTypes = async () => {
        if (opType && getValues()[KeyWordLocalization.DashboardPageOpTypeForm] == "") {
            const response = await di.get<SearchOperationTypesUseCase>(SearchOperationTypesUseCaseName).call({
                itemsPerPage: 20,
                page: 1,
                query: opType,
            });
            if (isLeft(response)) return;
            const parsed: AutoCompleteItem<MasterItemEntity> = {
                label: response.right.data[0].name,
                id: response.right.data[0].id,
                aditionalValue: response.right.data[0],
            };
            setValue(KeyWordLocalization.DashboardPageOpTypeForm, [parsed]);
        }
    }

    const _loadAiAgent = async () => {
        if (aiAgentQuery && getValues()[KeyWordLocalization.DashboardPageFormForm] == "") {
            const response = await di.get<GetAgentByIdUseCase>(GetAgentByIdUseCaseName).call(aiAgentQuery);
            if (isLeft(response)) return;
            const parsed: AutoCompleteItem<QAAgentEntity> = {
                label: response.right.name,
                id: response.right.id,
                aditionalValue: response.right,
            };
            setValue(KeyWordLocalization.DashboardPageFormForm, [parsed]);
        }
    }

    const _onSearchOpTypes = async (query: string): Promise<AutoCompleteItem<MasterItemEntity>[]> => {
        const response = await di.get<SearchOperationTypesUseCase>(SearchOperationTypesUseCaseName).call({
            itemsPerPage: 20,
            page: 1,
            query,
        });
        if (isLeft(response)) return [];
        const parsed = response.right.data.map((item) => {
            return {
                label: item.name,
                id: item.id,
                aditionalValue: item,
            };
        });
        return parsed;
    };

    const handleChangeOpType = (item: AutoCompleteItem<MasterItemEntity>) => {
        setValue(KeyWordLocalization.DashboardPageOpTypeForm, [item]);
        setValue(KeyWordLocalization.DashboardPageOpTypeFormId, item?.id);
        setValue(KeyWordLocalization.DashboardPageAiAgentId, "");
        setValue(KeyWordLocalization.DashboardPageFormForm, []);
        setValue(KeyWordLocalization.DashboardPageHumanAgentId, "");
        setValue(KeyWordLocalization.QAResumeEntityHumanAgentName, []);
        _onSubmit(getValues());
    };

    const _handleLoadCallTypes = () => di.get<GetCallTypesUseCase>(GetCallTypesUseCaseName).call();

    const _navigateToQAResults = (filters: { [key: string]: string }) => {
        const params = new URLSearchParams();
        params.append('page', '1');
        
        Object.entries(filters).forEach(([key, value]) => {
            if (value) {
                params.append(key, value);
            }
        });
        
        const url = `${routes.qa_resumes_list.relativePath}?${params.toString()}`;
        navigate(url);
    };

    const _handleClickCompleted = () => {
        _navigateToQAResults({ call_state: 'COMPLETED' });
    };

    const _handleClickNotRated = () => {
        _navigateToQAResults({ call_state: 'NOT_RATED' });
    };

    const _handleClickNegativeSentiment = () => {
        _navigateToQAResults({ sentiment: 'Negative' });
    };

    const _handleClickNeutralSentiment = () => {
        _navigateToQAResults({ sentiment: 'Neutral' });
    };

    const _handleClickPositiveSentiment = () => {
        _navigateToQAResults({ sentiment: 'Positive' });
    };


    useEffect(() => {
        _handleLoadDefaults();
    }, [dateFrom, dateTo, opType, humanAgentQuery, aiAgentQuery, callTypeQuery]);


    return {
        handleSubmit,
        _onSubmit,
        errors,
        register,
        i18n,
        _onSearchHuman,
        formInfo,
        handleChangeHumanAgent,
        onSearchAiAgent,
        handleChangeAiAgent,
        loaded,
        _handleSeeAgentDetails,
        colors,
        parsedChartData,
        watch,
        data,
        setData,
        _getPercentFeelings,
        handleChangeOpType,
        _onSearchOpTypes,
        handleChangeForm,
        typesCall,
        _handleClickCompleted,
        _handleClickNotRated,
        _handleClickNegativeSentiment,
        _handleClickNeutralSentiment,
        _handleClickPositiveSentiment,
    }
}

export default useDashboardPageViewModel;